import type { TicketAction } from '@rudy/shared';

/** once: one award ever (per season unless permanent) · per_ref: once per ref id (campaign, stream, video, quiz…)
 *  daily: once per calendar day · capped_daily: up to `dailyCap` per day · per_streak / weekly: see engine. */
export type RepeatRule =
  'once' | 'per_ref' | 'daily' | 'capped_daily' | 'weekly' | 'per_streak' | 'any';

export interface ActionRule {
  reward: number;
  permanent: boolean;
  repeat: RepeatRule;
  dailyCap?: number;
  /** Whether the VIP multiplier applies to this action (never to permanent or admin actions). */
  multiplied: boolean;
}

export interface SeasonRules {
  vipMultiplier: number;
  /** Extra tickets when a friend joins VIP (on top of `referral.reward` for the first ticket). */
  referralVipBonus: number;
  teamSize: number;
  teamMinTickets: number;
  streakLength: number;
  quizMaxReward: number;
  activityLotteryEvery: number;
  actions: Record<TicketAction, ActionRule>;
}

const r = (reward: number, repeat: RepeatRule, extra: Partial<ActionRule> = {}): ActionRule => ({
  reward,
  repeat,
  permanent: false,
  multiplied: true,
  ...extra,
});

/** TZ §4 defaults. Seasons store overrides in `seasons.rules_json`; merge with `resolveRules`. */
export const DEFAULT_RULES: SeasonRules = {
  vipMultiplier: 1.5,
  referralVipBonus: 3,
  teamSize: 4,
  teamMinTickets: 5,
  streakLength: 7,
  quizMaxReward: 5,
  activityLotteryEvery: 10,
  actions: {
    channel_subscribe: r(1, 'once', { permanent: true, multiplied: false }),
    vip_join: r(10, 'once', { permanent: true, multiplied: false }),
    follow_instagram: r(2, 'once'),
    follow_tiktok: r(2, 'once'),
    repost_story: r(3, 'per_ref'),
    stream_attend: r(2, 'per_ref'),
    referral: r(1, 'capped_daily', { dailyCap: 20 }),
    team_pool: r(2, 'once'),
    daily_streak: r(1, 'per_streak'),
    watch_video: r(1, 'per_ref'),
    quiz: r(1, 'per_ref'),
    price_guess: r(1, 'daily'),
    comment: r(1, 'capped_daily', { dailyCap: 3 }),
    wheel_sector_tickets: r(5, 'per_ref'),
    wheel_streak: r(3, 'per_ref'),
    activity_lottery: r(0, 'per_ref', { multiplied: false }),
    secret_task: r(3, 'weekly'),
    vip_lesson: r(1, 'per_ref', { multiplied: false }), // TZ §5: +1 each (members only, so no ×1.5)
    season_start_bonus: r(10, 'once', { multiplied: false }),
    season_end: r(0, 'any', { multiplied: false }),
    manual: r(0, 'any', { multiplied: false }),
  },
};

export type TaskGroup = 'instant' | 'social' | 'live' | 'daily' | 'friends' | 'vip';
export type Verification =
  | 'auto'
  | 'screenshot'
  | 'code_word'
  | 'server_timer'
  | 'answers'
  | 'price_feed'
  | 'listener'
  | 'admin';

export interface DefaultTask {
  key: TicketAction;
  group: TaskGroup;
  verification: Verification;
  secret?: boolean;
  config?: Record<string, unknown>;
}

/** Participant-visible tasks seeded for a new season. Titles come from copy `tasks.<key>`. */
export const DEFAULT_TASKS: DefaultTask[] = [
  { key: 'channel_subscribe', group: 'instant', verification: 'listener' },
  { key: 'vip_join', group: 'instant', verification: 'admin' },
  {
    key: 'follow_instagram',
    group: 'social',
    verification: 'screenshot',
    config: { handleRequired: true },
  },
  {
    key: 'follow_tiktok',
    group: 'social',
    verification: 'screenshot',
    config: { handleRequired: true },
  },
  {
    key: 'repost_story',
    group: 'social',
    verification: 'screenshot',
    config: { requirePublic: true },
  },
  { key: 'stream_attend', group: 'live', verification: 'code_word', config: { windowMinutes: 30 } },
  { key: 'daily_streak', group: 'daily', verification: 'auto' },
  {
    key: 'price_guess',
    group: 'daily',
    verification: 'price_feed',
    config: { asset: 'BTCUSDT', tolerancePct: 0.5, horizonMinutes: 60 },
  },
  { key: 'quiz', group: 'daily', verification: 'answers' },
  { key: 'watch_video', group: 'daily', verification: 'server_timer', config: { minWatchPct: 90 } },
  { key: 'comment', group: 'daily', verification: 'listener' },
  { key: 'referral', group: 'friends', verification: 'auto' },
  { key: 'team_pool', group: 'friends', verification: 'auto' },
  { key: 'secret_task', group: 'daily', verification: 'admin', secret: true },
];

export type SectorKind = 'physical' | 'cash' | 'tickets' | 'nothing';
export interface DefaultSector {
  key: string;
  kind: SectorKind;
  value: number;
  weight: number;
  stock: number | null;
}

/** Weekly wheel defaults (TZ §6). Base weights are relative; prize sectors get ticket weighting. */
export const DEFAULT_SECTORS: DefaultSector[] = [
  { key: 'macbook', kind: 'physical', value: 0, weight: 0.5, stock: 1 },
  { key: 'iphone', kind: 'physical', value: 0, weight: 1, stock: 2 },
  { key: 'airpods', kind: 'physical', value: 0, weight: 2.5, stock: 5 },
  { key: 'ps5', kind: 'physical', value: 0, weight: 0.5, stock: 1 },
  { key: 'cash50', kind: 'cash', value: 50, weight: 4, stock: null },
  { key: 'cash100', kind: 'cash', value: 100, weight: 2, stock: null },
  { key: 'cash200', kind: 'cash', value: 200, weight: 1, stock: null },
  { key: 'tickets5', kind: 'tickets', value: 5, weight: 18, stock: null },
  { key: 'nothing', kind: 'nothing', value: 0, weight: 70.5, stock: null },
];

/** Operational defaults stored in `settings` (admin-editable). */
export const DEFAULT_SETTINGS = {
  quiet_hours: { from: '23:00', to: '09:00' },
  frequency_cap_per_day: 2,
  push_rate_per_sec: 25,
  wheel_schedule: { weekday: 6, opens: '11:00', closes: '23:59', reminder: '20:00' },
  wheel_spins: { base: 1, vip: 2 },
  wheel_weight_cap: 5,
  leagues: { silver: 10, gold: 50 },
  league_up_bonus: { silver: 1, gold: 3 },
  winner_response_minutes: 10,
  stream_window_minutes: 30,
} as const;
